// ============================================================
// Chess Arena — Central Event Bus (Singleton)
// ============================================================

import { GameEventType } from './types';

/**
 * All event names the bus can carry.
 * Includes the GameEventType events from types.ts plus internal engine events.
 */
export type EventName =
  | GameEventType
  | 'piece_moved_complete'
  | 'spawn_ready'
  | 'wave_triggered'
  | 'match_end'
  | 'tutorial_step_complete'
  | 'cooldown_end'
  | 'premove_cancelled'
  | 'premove_executed'
  | 'king_captured'
  | 'timer_expired'
  | 'assets_loaded'
  | 'ability_activated'
  | 'ability_unlocked'
  | 'targeting_mode_start'
  | 'targeting_mode_cancel'
  | 'targeting_mode_confirm'
  | 'ability_bomb'
  | 'ability_freeze'
  | 'ability_shield'
  | 'ability_lightning';

type EventCallback = (data?: Record<string, unknown>) => void;

class EventBus {
  private listeners: Map<EventName, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event.
   */
  on(event: EventName, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Unsubscribe from an event.
   */
  off(event: EventName, callback: EventCallback): void {
    const set = this.listeners.get(event);
    if (set) {
      set.delete(callback);
      if (set.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit an event to all subscribers.
   */
  emit(event: EventName, data?: Record<string, unknown>): void {
    const set = this.listeners.get(event);
    if (set) {
      for (const cb of set) {
        cb(data);
      }
    }
  }

  /**
   * Remove all listeners (useful for scene teardown).
   */
  clear(): void {
    this.listeners.clear();
  }
}

/** Singleton event bus instance. */
const eventBus = new EventBus();
export default eventBus;
